import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Users, Baby, Heart, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

import { SOCIO_CATEGORIES } from "@/lib/categories";

const SCHOOLS = [
  "Instituto Hebreo",
  "Alumni Instituto Hebreo",
  "Maimonides",
  "Santiago College",
  "Craighouse",
  "Grange",
  "Otro Colegio",
  "Universidad no alumni",
];

function Field({ label, value, onChange, onBlur, type = "text", testId }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
      <Input type={type} className="h-12 text-base rounded-xl mt-1.5" value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined} data-testid={testId} />
    </div>
  );
}

function age(birthdate) {
  if (!birthdate) return null;
  const b = new Date(birthdate);
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
  return isNaN(a) ? null : a;
}

const IH_SCHOOLS = ["Instituto Hebreo", "Alumni Instituto Hebreo"];

// Propone la categoría de socio según edad, si es familia y si tiene hijos IH menores de 25.
function suggestCategory(fam) {
  const titAge = age(fam?.titular?.birthdate);
  const hasSpouse = !!(fam?.spouse && (fam.spouse.nombre || fam.spouse.rut));
  const children = fam?.children || [];
  const under25 = (c) => { const a = age(c.birthdate); return a === null || a < 25; };
  const hasIHChildUnder25 = children.some((c) => IH_SCHOOLS.includes(c.colegio) && under25(c));
  const hasChildUnder25 = children.some(under25);

  if (hasIHChildUnder25) return "Familia IAJAD";
  if (hasChildUnder25) return "Familia";
  if (titAge != null && titAge >= 80) return "Kabod";
  if (titAge != null && titAge >= 65) return hasSpouse ? "Senior Pareja" : "Senior";
  if (hasSpouse) return "Pareja";
  if (titAge != null && titAge >= 18 && titAge <= 24) return "Estudiante";
  return "Individual";
}

export default function StepFamily({ app, patch }) {
  const fam = app.family || { titular: {}, spouse: null, children: [], grandparents: [] };

  const setTitular = (k, v) => patch({ family: { ...fam, titular: { ...fam.titular, [k]: v } } });
  const setSpouse = (k, v) => patch({ family: { ...fam, spouse: { ...(fam.spouse || {}), [k]: v } } });
  const setFamField = (k, v) => patch({ family: { ...fam, [k]: v } });
  const toggleSpouse = () => patch({ family: { ...fam, spouse: fam.spouse ? null : { nombre: "" } } });

  const suggestedCategory = suggestCategory(fam);
  // Autocompleta la categoría sugerida solo si aún no hay ninguna elegida.
  useEffect(() => {
    if (suggestedCategory && !fam.titular?.categoria) {
      setTitular("categoria", suggestedCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedCategory, fam.titular?.categoria]);

  const norm = (r) => (r || "").replace(/[.\-\s]/g, "").toUpperCase();

  const [familyLoading, setFamilyLoading] = useState(false);

  const lookupTitularFamily = async (rut) => {
    if (!rut || norm(rut).length < 7) return;
    setFamilyLoading(true);
    try {
      const { data } = await api.get(`/rut-lookup-family`, { params: { rut } });
      if (!data.found) return;
      const t = fam.titular || {};
      const newTitular = {
        ...t,
        nombre: t.nombre || data.titular?.nombre || "",
        birthdate: t.birthdate || data.titular?.birthdate || "",
        email: t.email || data.titular?.email || "",
        telefono: t.telefono || data.titular?.telefono || "",
        categoria: t.categoria || data.titular?.categoria || "",
        rut,
      };
      // Cónyuge: solo si aún no hay uno cargado
      let newSpouse = fam.spouse;
      if (!newSpouse && data.spouse) {
        newSpouse = { nombre: data.spouse.nombre || "", rut: data.spouse.rut || "", birthdate: data.spouse.birthdate || "", email: data.spouse.email || "", telefono: data.spouse.telefono || "" };
      }
      // Hijos: agregar solo los que no estén ya (dedupe por RUT o nombre)
      const existing = [...(fam.children || [])];
      const have = new Set(existing.map((c) => norm(c.rut) || (c.nombre || "").trim().toLowerCase()));
      let added = 0;
      (data.children || []).forEach((c) => {
        const key = norm(c.rut) || (c.nombre || "").trim().toLowerCase();
        if (key && !have.has(key)) {
          have.add(key);
          existing.push({ id: uid(), nombre: c.nombre || "", rut: c.rut || "", birthdate: c.birthdate || "", colegio: c.colegio || "", relacion: c.relacion || "Hijo/a" });
          added++;
        }
      });
      patch({ family: { ...fam, titular: newTitular, spouse: newSpouse, children: existing } });
      toast.success(`Datos de la familia cargados desde la base EIM${added ? ` (${added} hijo/a agregados)` : ""}.`);
    } catch { /* silencioso */ } finally { setFamilyLoading(false); }
  };

  const addChild = () => patch({ family: { ...fam, children: [...(fam.children || []), { id: uid(), nombre: "", rut: "", birthdate: "", colegio: "", relacion: "Hijo/a" }] } });
  const setChild = (i, k, v) => { const c = [...fam.children]; c[i] = { ...c[i], [k]: v }; patch({ family: { ...fam, children: c } }); };
  const rmChild = (i) => patch({ family: { ...fam, children: fam.children.filter((_, x) => x !== i) } });

  const lookupSpouse = async (rut) => {
    if (!rut || rut.replace(/[.\-\s]/g, "").length < 7) return;
    try {
      const { data } = await api.get(`/rut-lookup`, { params: { rut } });
      if (!data.found) return;
      const sp = fam.spouse || {};
      patch({ family: { ...fam, spouse: {
        ...sp,
        nombre: sp.nombre || data.nombre || "",
        birthdate: sp.birthdate || data.birthdate || "",
        email: sp.email || data.email || "",
        telefono: sp.telefono || data.telefono || "",
        rut,
      } } });
      toast.success("Datos del cónyuge cargados desde la base EIM.");
    } catch { /* silencioso */ }
  };

  const lookupChild = async (i, rut) => {
    if (!rut || rut.replace(/[.\-\s]/g, "").length < 7) return;
    try {
      const { data } = await api.get(`/rut-lookup`, { params: { rut } });
      if (!data.found && !data.is_ih_student) return;
      const c = [...fam.children];
      const ch = { ...c[i], rut };
      if (data.found) {
        ch.nombre = ch.nombre || data.nombre || "";
        ch.birthdate = ch.birthdate || data.birthdate || "";
      }
      if (data.is_ih_student && !ch.colegio) ch.colegio = "Instituto Hebreo";
      c[i] = ch;
      patch({ family: { ...fam, children: c } });
      if (data.found || data.is_ih_student) toast.success("Datos del hijo/a cargados desde la base EIM.");
    } catch { /* silencioso */ }
  };

  const addGp = () => patch({ family: { ...fam, grandparents: [...(fam.grandparents || []), { id: uid(), nombre: "", rut: "", relacion: "Abuelo/a" }] } });
  const setGp = (i, k, v) => { const g = [...(fam.grandparents || [])]; g[i] = { ...g[i], [k]: v }; patch({ family: { ...fam, grandparents: g } }); };
  const rmGp = (i) => patch({ family: { ...fam, grandparents: fam.grandparents.filter((_, x) => x !== i) } });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Grupo familiar</h1>
        <p className="text-muted-foreground">Revisa que esta información siga vigente. Corrige o completa lo que falte.</p>
      </div>

      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold mb-4"><Users className="w-5 h-5 text-accent" /> Titular</h2>
        {familyLoading && (
          <div className="flex items-center gap-3 bg-secondary/70 border border-border rounded-xl px-4 py-3 mb-4" data-testid="family-lookup-loading">
            <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
            <p className="text-sm text-primary font-medium">Danos unos segundos mientras te buscamos en nuestra base de datos…</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nombre" value={fam.titular?.nombre} onChange={(v) => setTitular("nombre", v)} testId="titular-nombre" />
          <Field label="RUT" value={fam.titular?.rut} onChange={(v) => setTitular("rut", v)} onBlur={lookupTitularFamily} testId="titular-rut" />
          <Field label="Fecha de nacimiento" type="date" value={fam.titular?.birthdate} onChange={(v) => setTitular("birthdate", v)} testId="titular-birthdate" />
          <Field label="Email" value={fam.titular?.email} onChange={(v) => setTitular("email", v)} />
          <Field label="Teléfono" value={fam.titular?.telefono} onChange={(v) => setTitular("telefono", v)} />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Categoría de socio</Label>
            <Select value={fam.titular?.categoria || ""} onValueChange={(v) => setTitular("categoria", v)}>
              <SelectTrigger className="h-12 text-base rounded-xl mt-1.5" data-testid="titular-categoria">
                <SelectValue placeholder="Selecciona tu categoría" />
              </SelectTrigger>
              <SelectContent>
                {SOCIO_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`categoria-option-${c.value}`}>
                    <span className="font-medium">{c.value}</span>
                    <span className="text-muted-foreground"> · {c.uf} UF</span>
                    <span className="block text-xs text-muted-foreground">{c.desc}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {suggestedCategory && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="categoria-sugerida">
                Sugerida según tu grupo familiar: <strong className="text-primary">{suggestedCategory}</strong>
                {fam.titular?.categoria !== suggestedCategory && (
                  <button type="button" onClick={() => setTitular("categoria", suggestedCategory)}
                    className="ml-2 text-accent font-semibold hover:underline" data-testid="btn-usar-sugerida">
                    Usar sugerida
                  </button>
                )}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><Heart className="w-5 h-5 text-accent" /> Cónyuge / pareja</h2>
          <Button variant="outline" size="sm" onClick={toggleSpouse} className="rounded-lg" data-testid="btn-toggle-spouse">
            {fam.spouse ? "Quitar" : "Agregar"}
          </Button>
        </div>
        {fam.spouse && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" value={fam.spouse?.nombre} onChange={(v) => setSpouse("nombre", v)} testId="spouse-nombre" />
            <Field label="RUT" value={fam.spouse?.rut} onChange={(v) => setSpouse("rut", v)} onBlur={lookupSpouse} testId="spouse-rut" />
            <Field label="Fecha de nacimiento" type="date" value={fam.spouse?.birthdate} onChange={(v) => setSpouse("birthdate", v)} />
            <Field label="Email" value={fam.spouse?.email} onChange={(v) => setSpouse("email", v)} />
            <Field label="Teléfono" value={fam.spouse?.telefono} onChange={(v) => setSpouse("telefono", v)} />
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><Baby className="w-5 h-5 text-accent" /> Hijos</h2>
          <Button variant="outline" size="sm" onClick={addChild} className="rounded-lg" data-testid="btn-add-child"><Plus className="w-4 h-4 mr-1" /> Agregar hijo/a</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">El RUT de los hijos es importante: se utiliza para enrolamiento y control de acceso.</p>
        <div className="space-y-4">
          {(fam.children || []).map((c, i) => (
            <div key={c.id || i} className="border border-border rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-primary">Hijo/a {i + 1}{age(c.birthdate) != null ? ` · ${age(c.birthdate)} años` : ""}</span>
                {age(c.birthdate) != null && age(c.birthdate) >= 25 && (
                  <span className="ml-auto mr-3 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`child-over25-${i}`}>No cuenta para el grupo familiar (25+ años)</span>
                )}
                <button onClick={() => rmChild(i)} className="text-destructive" data-testid={`btn-remove-child-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nombre" value={c.nombre} onChange={(v) => setChild(i, "nombre", v)} testId={`child-nombre-${i}`} />
                <Field label="RUT" value={c.rut} onChange={(v) => setChild(i, "rut", v)} onBlur={(v) => lookupChild(i, v)} testId={`child-rut-${i}`} />
                <Field label="Fecha de nacimiento" type="date" value={c.birthdate} onChange={(v) => setChild(i, "birthdate", v)} testId={`child-birthdate-${i}`} />
                <Field label="Relación familiar" value={c.relacion} onChange={(v) => setChild(i, "relacion", v)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Colegio / establecimiento</Label>
                  <Select value={c.colegio || ""} onValueChange={(v) => setChild(i, "colegio", v)}>
                    <SelectTrigger className="h-12 text-base rounded-xl mt-1.5" data-testid={`child-colegio-${i}`}>
                      <SelectValue placeholder="Selecciona el colegio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOOLS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`child-${i}-colegio-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {c.colegio === "Alumni Instituto Hebreo" && (
                  <Field label="Universidad" value={c.universidad} onChange={(v) => setChild(i, "universidad", v)} testId={`child-universidad-${i}`} />
                )}
                {c.colegio === "Otro Colegio" && (
                  <Field label="Nombre del colegio" value={c.colegio_otro} onChange={(v) => setChild(i, "colegio_otro", v)} testId={`child-colegio-otro-${i}`} />
                )}
              </div>
            </div>
          ))}
          {(fam.children || []).length === 0 && <p className="text-sm text-muted-foreground">No has agregado hijos.</p>}
        </div>

        {(fam.children || []).some((c) => c.colegio === "Instituto Hebreo") && (
          <div className="mt-4 bg-secondary/50 border border-border rounded-xl p-4" data-testid="ih-family-beca">
            <p className="text-sm font-semibold text-primary mb-1">Beca del Instituto Hebreo</p>
            <p className="text-xs text-muted-foreground mb-3">La beca del IH se otorga a la <strong>familia</strong> (no por cada hijo/a). Respóndela una sola vez.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">¿La familia tiene beca del Instituto Hebreo?</Label>
                <Select value={fam.ih_beca === true ? "si" : fam.ih_beca === false ? "no" : ""} onValueChange={(v) => setFamField("ih_beca", v === "si")}>
                  <SelectTrigger className="h-12 text-base rounded-xl mt-1.5" data-testid="family-ihbeca">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Sí</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {fam.ih_beca === true && (
                <Field label="% de beca IH" type="number" value={fam.ih_beca_pct} onChange={(v) => setFamField("ih_beca_pct", v)} testId="family-ihbeca-pct" />
              )}
            </div>
            {fam.ih_beca === true && (
              <div className="mt-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="ih-beca-consent-label">
                  <Checkbox checked={fam.ih_beca_consent === true}
                    onCheckedChange={(v) => setFamField("ih_beca_consent", v === true)}
                    className="mt-0.5" data-testid="ih-beca-consent" />
                  <span className="text-sm text-slate-700">
                    Al informar que poseo beca en el IH, <strong>autorizo a la asistente social del IH a ratificar dicho porcentaje</strong> con el comité de becas del Estadio Israelita.
                  </span>
                </label>
                {fam.ih_beca_consent !== true && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ih-beca-consent-warning">
                    <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900">Si no marcas esta autorización, <strong>la beca del IH no se considerará</strong> en esta solicitud.</p>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">La asistente social del Instituto Hebreo confirmará esta beca.</p>
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Abuelos / antecedentes familiares</h2>
          <Button variant="outline" size="sm" onClick={addGp} className="rounded-lg" data-testid="btn-add-gp"><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
        <div className="space-y-4">
          {(fam.grandparents || []).map((g, i) => (
            <div key={g.id || i} className="border border-border rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-primary">Familiar {i + 1}</span>
                <button onClick={() => rmGp(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Nombre" value={g.nombre} onChange={(v) => setGp(i, "nombre", v)} />
                <Field label="RUT" value={g.rut} onChange={(v) => setGp(i, "rut", v)} />
                <Field label="Relación" value={g.relacion} onChange={(v) => setGp(i, "relacion", v)} />
              </div>
            </div>
          ))}
          {(fam.grandparents || []).length === 0 && <p className="text-sm text-muted-foreground">Opcional.</p>}
        </div>
      </section>
    </div>
  );
}
